import { hslaBaseColors, hslaColors, hslaGreys, themeColors } from '../colors';

import { u } from '../utilities';
import {
  fontWeights,
  fontSettings,
  radii,
  shadows,
  spaces,
  textSizes,
  unit,
  gradients,
} from '../variables';

type VarsObject = Record<string, string | number>;

export const getCssVarsFromObject = (
  prefix: string,
  varsObject: VarsObject
): VarsObject => {
  const resultObj = {};

  Object.entries(varsObject).forEach(([varName, varValue]) => {
    const namePrefix = prefix ? `${prefix}-` : '';
    const value = `--lns-${namePrefix + varName}`;

    resultObj[value] = varValue;
  });

  return resultObj;
};

const getTextSizesObject = (): VarsObject => {
  const resultObj = {};

  Object.entries(textSizes).forEach(([textSizeKey, textSizeValue]) => {
    const fontSizesObj = {};
    const lineHeightsObj = {};
    const letterSpacingObj = {};
    const fontSizeValue = `fontSize-${textSizeKey}`;
    const lineHeightValue = `lineHeight-${textSizeKey}`;
    const letterSpacingValue = `letterSpacing-${textSizeKey}`;

    fontSizesObj[fontSizeValue] = u(textSizeValue.fontSize);
    fontSizesObj[lineHeightValue] = textSizeValue.lineHeight;
    fontSizesObj[letterSpacingValue] = textSizeValue.letterSpacing;

    Object.assign(resultObj, fontSizesObj, lineHeightsObj, letterSpacingObj);
  });

  return resultObj;
};

const getSpacesObject = (): VarsObject => {
  const resultObj = {};

  Object.entries(spaces).forEach(([spaceName, spaceValue]) => {
    const value = `space-${spaceName}`;

    resultObj[value] = u(spaceValue);
  });

  return resultObj;
};

const getColorsObject = (): VarsObject => {
  const colorsObject = {};

  Object.keys({ ...hslaBaseColors, ...hslaGreys }).forEach(name => {
    const value = name;

    colorsObject[value] =
      `hsla(${hslaColors[name].h},${hslaColors[name].s}%,${hslaColors[name].l}%,${hslaColors[name].a})`;
  });

  return colorsObject;
};

const getThemeColorsObject = (): VarsObject => {
  const reducer = (colors, prefix) =>
    Object.keys(colors).reduce((accumulator, name) => {
      const color = colors[name];
      const value = `${prefix}-color-${name}`;

      accumulator[value] = color.ads
        ? `var(${color.ads}, hsla(${color.h},${color.s}%,${color.l}%,${color.a}))`
        : `hsla(${color.h},${color.s}%,${color.l}%,${color.a})`;

      return accumulator;
    }, {});

  return {
    ...reducer(themeColors.light, 'themeLight'),
    ...reducer(themeColors.dark, 'themeDark'),
  };
};

export const unitVariables = getCssVarsFromObject(undefined, {
  unit: `${unit / 16}rem`,
});

export const fontWeightVariables = getCssVarsFromObject(
  'fontWeight',
  fontWeights
);

export const fontSettingVariables = getCssVarsFromObject(
  'fontSetting',
  fontSettings
);

export const textSizeVariables = getCssVarsFromObject(
  undefined,
  getTextSizesObject()
);

export const radiusVariables = getCssVarsFromObject('radius', radii);

export const shadowVariables = getCssVarsFromObject('shadow', shadows);

export const spaceVariables = getCssVarsFromObject(
  undefined,
  getSpacesObject()
);

export const formFieldVariables = getCssVarsFromObject(undefined, {
  formFieldBorderWidth: '1px',
  formFieldBorderWidthFocus: '2px',
  formFieldHeight: u(4.5),
  formFieldRadius: 'var(--lns-radius-175)',
  formFieldHorizontalPadding: u(2),
  formFieldBorderShadow: `
    inset 0 0 0 var(--lns-formFieldBorderWidth) var(--lns-color-formFieldBorder)
  `,
  formFieldBorderShadowFocus: `
    inset 0 0 0 var(--lns-formFieldBorderWidthFocus) var(--lns-color-blurple),
    0 0 0 var(--lns-formFieldBorderWidthFocus) var(--lns-color-focusRing)
  `,
  formFieldBorderShadowError: `
    inset 0 0 0 var(--lns-formFieldBorderWidthFocus) var(--lns-color-danger),
    0 0 0 var(--lns-formFieldBorderWidthFocus) var(--lns-color-orangeLight)
  `,
});

const colorVariables = getCssVarsFromObject('color', getColorsObject());

const themeColorVariables = getCssVarsFromObject(
  undefined,
  getThemeColorsObject()
);

export const gradientVariables = getCssVarsFromObject('gradient', gradients);

const sizingVariables = [
  unitVariables,
  textSizeVariables,
  radiusVariables,
  shadowVariables,
  spaceVariables,
  formFieldVariables,
];

export const getSizingVariablesCssVarsObject = (): VarsObject => {
  return Object.assign({}, ...sizingVariables);
};

export const getVariablesCssVarsObject = (): VarsObject => {
  return Object.assign(
    {},
    fontWeightVariables,
    ...sizingVariables,
    fontSettingVariables
  );
};

export const getColorsCssVarsObject = (): VarsObject => {
  return {
    ...colorVariables,
    ...themeColorVariables,
    ...gradientVariables,
  };
};

const assignLightThemeColors = () => {
  return Object.keys(themeColors.light).map(
    color => `--lns-color-${color}: var(--lns-themeLight-color-${color});`
  );
};
const assignDarkThemeColors = () => {
  return Object.keys(themeColors.dark).map(
    color => `--lns-color-${color}: var(--lns-themeDark-color-${color});`
  );
};

export const getThemeStylesString = (customRootElement = ':root') => {
  const rootElement = customRootElement || ':root';

  return `
    ${rootElement},
    .theme-light,
    [data-lens-theme="light"] {
      ${assignLightThemeColors().join('')}
    }

    .theme-dark,
    [data-lens-theme="dark"] {
      ${assignDarkThemeColors().join('')}
    }
  `;
};

export const getThemeStyles = () => {
  const style = document.createElement('style');

  style.innerHTML = getThemeStylesString();
  document.head.appendChild(style);
};

export const getSizingCssVarsDeclarations = () => {
  const result = Object.entries(getSizingVariablesCssVarsObject()).map(
    cssVar => `${cssVar[0]}:${cssVar[1]};`
  );

  return result.join('');
};

export const getAllCssVarsString = customRootElement => {
  const result = [];
  const rootElement = customRootElement || ':root';

  Object.entries(getVariablesCssVarsObject()).forEach(cssVar => {
    result.push(`${cssVar[0]}:${cssVar[1]};`);
  });

  Object.entries(getColorsCssVarsObject()).forEach(cssVar => {
    result.push(`${cssVar[0]}:${cssVar[1]};`);
  });

  return `
    ${rootElement} {
      ${result.join('')}
    }
  `;
};
